import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {map, Observable} from 'rxjs';
import {GdxCooperativeRegistryDto} from './business.models';
import {BusinessAddressOptionDto, BusinessJuristicTypeDto, BusinessTypeDto, CertificateType, GdxJuristicProfileResponse, GdxSmceProfileResponse, ProductionSiteDto} from '../../../../models/generated-models';

export interface BusinessCertificateOptionDto {
  id?: number;
  farmId?: number | null;
  certificateType?: CertificateType;
  title?: string;
  certificateNo?: string;
  ownerName?: string;
  taxId?: string;
  standardCode?: string;
  standardName?: string;
  productName?: string;
  description?: string;
  certificateKey?: string;
  displayLabel?: string;
  status?: 'active' | 'expired';
  canEdit?: boolean;
  address?: BusinessAddressOptionDto;
  startDate?: string;
  endDate?: string;
  /** สถานที่ผลิต from thaitraces (work item 176); absent on self-created and legacy certificates. */
  site?: ProductionSiteDto;
}

@Injectable({providedIn: 'root'})
export class BusinessApiService {
  private readonly http = inject(HttpClient);

  /** The ประเภทธุรกิจ options; {@code includeInactive} adds switched-off types so their titles still resolve. */
  getBusinessTypes(includeInactive = false): Observable<BusinessTypeDto[]> {
    const params = includeInactive ? {includeInactive: 'true'} : undefined;
    return this.http.get<BusinessTypeDto[]>(`${environment.serverUrl}/api/businesses/business-types`, {params});
  }

  searchAddresses(search: string, limit = 10): Observable<BusinessAddressOptionDto[]> {
    return this.http.get<BusinessAddressOptionDto[]>(`${environment.serverUrl}/api/businesses/address-options`, {
      params: {search, limit}
    });
  }

  getAddressBySubdistrictId(subdistrictId: number, zipcode?: string | null): Observable<BusinessAddressOptionDto> {
    let params = new HttpParams();
    if (zipcode?.trim()) {
      params = params.set('zipcode', zipcode.trim());
    }
    return this.http.get<BusinessAddressOptionDto>(
      `${environment.serverUrl}/api/businesses/address-options/${subdistrictId}`,
      {params}
    );
  }

  getAddressBySubdistrictCode(
    subdistrictCode: string,
    fallback?: {subdistrict?: string; district?: string; province?: string; zipcode?: string}
  ): Observable<BusinessAddressOptionDto> {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(fallback ?? {})) {
      if (value?.trim()) {
        params = params.set(key, value.trim());
      }
    }
    return this.http.get<BusinessAddressOptionDto>(
      `${environment.serverUrl}/api/businesses/address-options/by-code/${encodeURIComponent(subdistrictCode)}`,
      {params}
    );
  }

  getJuristicTypes(): Observable<BusinessJuristicTypeDto[]> {
    return this.http.get<BusinessJuristicTypeDto[]>(`${environment.serverUrl}/api/businesses/juristic-types`);
  }

  findJuristicProfile(juristicId: string): Observable<GdxJuristicProfileResponse> {
    return this.http.get<GdxJuristicProfileResponse>(`${environment.serverUrl}/api/gdx/juristic-profile`, {
      params: {juristicId}
    });
  }

  findOwnJuristicProfile(juristicId: string): Observable<GdxJuristicProfileResponse> {
    return this.http.get<GdxJuristicProfileResponse>(`${environment.serverUrl}/api/gdx/own/juristic-profile`, {
      params: {juristicId}
    });
  }

  findCooperativeRegistry(registryNo: string): Observable<GdxCooperativeRegistryDto> {
    return this.http.get<GdxCooperativeRegistryDto>(`${environment.serverUrl}/api/gdx/cooperative-registry`, {
      params: {registryNo}
    });
  }

  findSmceProfile(number: string): Observable<GdxSmceProfileResponse> {
    return this.http.get<GdxSmceProfileResponse>(`${environment.serverUrl}/api/gdx/smce`, {
      params: {number}
    });
  }

  findOwnSmceProfile(number: string): Observable<GdxSmceProfileResponse> {
    return this.http.get<GdxSmceProfileResponse>(`${environment.serverUrl}/api/gdx/own/smce`, {
      params: {number}
    });
  }

  listCertificates(): Observable<BusinessCertificateOptionDto[]> {
    return this.http.get<BusinessCertificateOptionDto[]>(`${environment.serverUrl}/api/certificates`)
      .pipe(map((items) => (items ?? []).map((item) => ({
        ...item,
        certificateKey: item.id != null
          ? `${(item.certificateType ?? 'UNKNOWN').toUpperCase()}:${item.id}`
          : undefined,
        displayLabel: this.formatCertificateLabel(item)
      }))));
  }

  private formatCertificateLabel(certificate: BusinessCertificateOptionDto): string {
    const typeLabel = this.formatCertificateType(certificate.certificateType);
    const parts = [
      typeLabel ? `[${typeLabel}]` : null,
      `${certificate.title ?? ''}`.trim(),
      `${certificate.certificateNo ?? ''}`.trim(),
      `${certificate.ownerName ?? ''}`.trim()
    ].filter((item): item is string => !!item && item.trim().length > 0);

    return parts.length > 0 ? parts.join(' | ') : 'ยังไม่ระบุ';
  }

  private formatCertificateType(type?: string): string {
    const normalized = `${type ?? ''}`.trim().toUpperCase();
    if (normalized === 'LEGACY') {
      return 'ดึงมา';
    }
    if (normalized === 'SELF_CREATED' || normalized === 'SELF') {
      return 'สร้างเอง';
    }
    return normalized ? `ประเภท ${normalized}` : '';
  }
}
